<script setup>
const props = defineProps({
  lowSrc: { type: String, required: true },
  src: { type: String, required: true },
  alt: { type: String, default: "" },
  width: { type: [Number, String], default: undefined },
  height: { type: [Number, String], default: undefined },
});

const lowEl = ref(null);
const highEl = ref(null);
const low = ref("loading");
const high = ref("loading");
const instant = ref(false);

const ready = (el) => el?.complete && el.naturalWidth > 0;

// Картинки приходят в SSR-разметке и могут загрузиться до гидрации — тогда load уже не придёт
onMounted(() => {
  if (ready(lowEl.value)) low.value = "ok";
  if (ready(highEl.value)) {
    high.value = "ok";
    instant.value = true;
  }
});

watch(() => props.lowSrc, () => (low.value = "loading"));
watch(() => props.src, () => {
  high.value = "loading";
  instant.value = false;
});

const pending = computed(
  () => low.value !== "ok" && high.value !== "ok" && (low.value === "loading" || high.value === "loading")
);
</script>

<template>
  <div class="progressive" :class="{ 'progressive--pending': pending }">
    <img
      ref="lowEl"
      :src="lowSrc"
      alt=""
      aria-hidden="true"
      class="progressive__img"
      :class="{ 'progressive__img--hidden': low === 'fail' }"
      loading="lazy"
      decoding="async"
      :width="width"
      :height="height"
      @load="low = 'ok'"
      @error="low = 'fail'"
    />
    <!-- lazy обязателен: карусели каталога держат в DOM по картинке на каждый цвет -->
    <img
      ref="highEl"
      :src="src"
      :alt="alt"
      class="progressive__img progressive__high"
      :class="{
        'progressive__high--shown': high === 'ok',
        'progressive__high--instant': instant,
      }"
      loading="lazy"
      decoding="async"
      :width="width"
      :height="height"
      @load="high = 'ok'"
      @error="high = 'fail'"
    />
  </div>
</template>

<style scoped>
.progressive {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: var(--el-fill-color-light);
}

.progressive--pending::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--el-bg-color) 65%, transparent), transparent);
  transform: translateX(-100%);
  animation: progressive-shimmer 1.4s ease-in-out infinite;
}

.progressive__img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.progressive__img--hidden {
  visibility: hidden;
}

.progressive__high {
  opacity: 0;
  transition: opacity 0.3s ease;
}

.progressive__high--shown {
  opacity: 1;
}

.progressive__high--instant {
  transition: none;
}

@keyframes progressive-shimmer {
  to {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .progressive--pending::before {
    animation: none;
  }

  .progressive__high {
    transition: none;
  }
}
</style>
